import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import { Box, Button, Stack, Step, StepButton, Stepper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { includes, some } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { JsonParam, StringParam } from 'use-query-params';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';
import { getLinkToJobsPage } from 'utils/useNavigationToJobsPage';
import { usePermissions } from 'utils/usePermissions';
import { multiplexPipelineRunPermissions } from '../Pipelines/Multiplex/useCanRunMultiplexPipeline';
import { WorkflowTab } from '../useStudyDashboardTabState';
import { buildItEmailBody, getItEmailSubject, getItNotes } from './cliInstructionItNotes';
import { CLIInstructionsProps } from './cliInstructionTypes';
import ItEmailDialog from './ItEmailDialog';
import UploadAsyncIngestHandoff from './UploadAsyncIngestHandoff';
import UploadStepInfoCard from './UploadStepInfoCard';
import { buildCliInstructionSteps } from './useCliInstructionSteps';

const trackingActionButtonSx = {
  alignSelf: 'flex-start',
  minWidth: 0,
  px: 0.75,
  py: 0.25,
};

const CLIInstructions = ({
  configureStep,
  credentialMode = 'temporary-nucleai',
  connectStepContent,
  initialStepIndex = 0,
  itNotesDestination,
  selectedOs,
  setSelectedOs,
  defaultOperatingSystem,
  studyId,
  title,
  uploadStepContent,
  uploadStepTitle,
  validateStep,
}: CLIInstructionsProps) => {
  const [activeStep, setActiveStep] = useState(initialStepIndex);
  const [isItEmailDialogOpen, setIsItEmailDialogOpen] = useState(false);
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const resolvedStudyId = studyId || '';
  const resolvedLabId = labId || '';
  const { data: stainTypesInStudy } = useStudyStainTypeIds(resolvedStudyId, { enabled: Boolean(resolvedStudyId) });
  const canViewWorkflow =
    includes(stainTypesInStudy, MULTIPLEX_STAIN_ID) &&
    hasPermission(Permission.ViewWorkflowMonitor) &&
    some(multiplexPipelineRunPermissions, hasPermission);
  const canViewJobs = hasPermission(Permission.ViewJobs);

  useEffect(() => {
    setActiveStep(initialStepIndex);
  }, [initialStepIndex]);

  const itNotes = getItNotes(credentialMode, itNotesDestination);
  const itEmailSubject = getItEmailSubject(credentialMode);
  const itEmailBody = buildItEmailBody(itNotes, credentialMode);
  const mailtoHref = `mailto:?subject=${encodeURIComponent(itEmailSubject)}&body=${encodeURIComponent(itEmailBody)}`;
  const steps = buildCliInstructionSteps({
    configureStep,
    connectStepContent,
    itNotes,
    onOpenSummary: () => setIsItEmailDialogOpen(true),
    selectedOs,
    defaultOperatingSystem,
    setSelectedOs,
    studyId: resolvedStudyId,
    uploadStepContent,
    uploadTrackingContent: studyId ? (
      <Stack spacing={1}>
        <UploadAsyncIngestHandoff studyId={resolvedStudyId} />
        {canViewWorkflow ? (
          <UploadStepInfoCard
            icon={<AssignmentTurnedInOutlinedIcon color="success" fontSize="small" />}
            tone="workflow"
            title="Review the multiplex workflow"
            description="For mIF studies, use the workflow tab to inspect downstream pipeline status after ingest and case assignment."
          >
            <Button
              size="small"
              variant="text"
              component={RouterLink}
              sx={trackingActionButtonSx}
              to={{
                pathname: '/procedures',
                search: encodeQueryParamsUsingSchema(
                  {
                    filters: { studyId: resolvedStudyId },
                    labId: resolvedLabId,
                    studyDashboardTab: WorkflowTab,
                  },
                  {
                    filters: JsonParam,
                    labId: StringParam,
                    studyDashboardTab: StringParam,
                  }
                ),
              }}
            >
              Open workflow
            </Button>
          </UploadStepInfoCard>
        ) : null}
        {canViewJobs ? (
          <UploadStepInfoCard
            icon={<ManageSearchOutlinedIcon color="warning" fontSize="small" />}
            tone="jobs"
            title="Inspect ingest jobs"
            description="Use the jobs page when you need operational details for ingest jobs, including job status, failures, and retry context."
          >
            <Button
              size="small"
              variant="text"
              component={RouterLink}
              sx={trackingActionButtonSx}
              to={getLinkToJobsPage({
                labId: resolvedLabId,
                jobsFilters: { studyId: resolvedStudyId, types: [JobType.Ingest, JobType.BatchIngest] },
              })}
            >
              View ingest jobs
            </Button>
          </UploadStepInfoCard>
        ) : null}
      </Stack>
    ) : undefined,
    uploadStepTitle,
    validateStep,
  });
  const activeStepConfig = steps[activeStep];
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box
      data-testid="cli-instructions"
      sx={{
        px: 2,
        py: 1.5,
      }}
    >
      <Stack
        data-testid="cli-instructions-sticky-top"
        spacing={2}
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: 1,
          flexShrink: 0,
          pb: 1.5,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <TerminalOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">{title}</Typography>
          </Stack>
        </Box>

        <Stepper
          data-testid="cli-instructions-stepper"
          nonLinear
          activeStep={activeStep}
          alternativeLabel
          sx={{
            backgroundColor: 'background.paper',
            maxWidth: '100%',
            minWidth: 0,
            pb: 1,
            '& .MuiStep-root': {
              minWidth: 0,
            },
            '& .MuiStepLabel-label': {
              whiteSpace: 'normal',
            },
          }}
        >
          {steps.map((step, index) => (
            <Step key={step.title} completed={step.isReady}>
              <StepButton
                color="inherit"
                optional={
                  step.isReady ? (
                    <Typography variant="caption" color="success">
                      Ready
                    </Typography>
                  ) : undefined
                }
                onClick={() => setActiveStep(index)}
              >
                {step.title}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Stack>

      <InstructionSection icon={activeStepConfig.icon} title={`${activeStep + 1}. ${activeStepConfig.title}`}>
        {activeStepConfig.content}
        <Stack
          data-testid="cli-instructions-sticky-bottom"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          sx={(theme) => ({
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            pt: 1.5,
            pb: 0.5,
            backgroundColor: theme.palette.background.paper,
          })}
        >
          <Button variant="outlined" disabled={isFirstStep} onClick={() => setActiveStep(activeStep - 1)}>
            Back
          </Button>
          {!isLastStep ? (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="contained" onClick={() => setActiveStep(activeStep + 1)}>
                Next
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </InstructionSection>
      <ItEmailDialog
        open={isItEmailDialogOpen}
        onClose={() => setIsItEmailDialogOpen(false)}
        subject={itEmailSubject}
        body={itEmailBody}
        mailtoHref={mailtoHref}
      />
    </Box>
  );
};

const InstructionSection = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={(theme) => ({
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: alpha(theme.palette.background.paper, 0.72),
      p: 1.5,
    })}
  >
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="subtitle2">{title}</Typography>
      </Stack>
      {children}
    </Stack>
  </Box>
);

export default CLIInstructions;
